import * as Sentry from '@sentry/angular';

/**
 * Subset of the `@sentry/angular` surface that `initSentry` uses.
 * Extracted as an injectable seam so tests can substitute a stub — the real
 * module's exports are non-writable ES bindings and cannot be `spyOn`'d.
 */
export interface SentryApi {
  init: typeof Sentry.init;
  browserTracingIntegration: typeof Sentry.browserTracingIntegration;
}

const defaultSentryApi: SentryApi = {
  init: Sentry.init,
  browserTracingIntegration: Sentry.browserTracingIntegration,
};

/**
 * Initializes Sentry when a DSN is configured.
 *
 * Extracted into its own function so it can be unit-tested without the
 * side effects of `main.ts` (which calls bootstrapApplication at module load).
 *
 * @returns `true` if Sentry was initialized, `false` otherwise.
 */
export function initSentry(
  env: { sentryDsn: string; production: boolean },
  api: SentryApi = defaultSentryApi,
): boolean {
  if (!env.sentryDsn) {
    return false;
  }

  api.init({
    dsn: env.sentryDsn,
    environment: env.production ? 'production' : 'development',
    integrations: [
      api.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.2,
  });

  return true;
}
